import React from "react";
import { Box, Chip, Collapse, IconButton, List, ListItemButton, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getAssetHierarchyPalette, getIconButtonSx, getStatusChipSx, getTypeChipSx } from "../assetHierarchyStyles";
import { getAssetNodeTypeLabel } from "../cmmsMessages";
import messages from "../messages";

// `null` keeps every node. Applied before anything counts children, so a branch whose children
// are all filtered out shows no chevron.
const keepVisible = (nodes, visibleIds) => (visibleIds ? nodes.filter((node) => visibleIds.has(node.id)) : nodes);

function AssetHierarchyTreeNode({ node, level, selectedNodeId, onSelectNode, expandedIds, onToggleNode, visibleIds }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const children = keepVisible(node.children ?? [], visibleIds);
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedNodeId === node.id;

  const handleToggle = (event) => {
    event.stopPropagation();
    if (hasChildren) {
      onToggleNode(node.id);
    }
  };

  return (
    <Box>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelectNode(node)}
        // Rows the way an OEE DataGrid draws them: no box of their own on the tile, a dark rule
        // between them. The green edge is the one CMMS addition, and marks the selected node.
        sx={{
          minHeight: 56,
          py: 1,
          pl: 1 + level * 2,
          pr: 1.25,
          borderBottom: `1px solid ${cmmsColors.border}`,
          borderLeft: `3px solid ${isSelected ? cmmsColors.accent : "transparent"}`,
          borderRadius: 0,
          backgroundColor: isSelected ? cmmsColors.selected : "transparent",
          color: cmmsColors.text,
          "&:hover": {
            backgroundColor: cmmsColors.surfaceHover,
          },
          "&.Mui-selected": {
            backgroundColor: cmmsColors.selected,
          },
          "&.Mui-selected:hover": {
            backgroundColor: cmmsColors.selectedHover,
          },
        }}
      >
        {hasChildren &&
          <IconButton
            aria-label={isExpanded ? "Collapse node" : "Expand node"}
            edge="start"
            onClick={handleToggle}
            size="small"
            sx={{ ...getIconButtonSx(cmmsColors), mr: 0.5, width: 30, height: 30 }}
          >
            {isExpanded && <ExpandMoreIcon fontSize="small" />}
            {!isExpanded && <ChevronRightIcon fontSize="small" />}
          </IconButton>
        }
        {!hasChildren && <Box sx={{ width: 30, height: 30, mr: 0.5, flexShrink: 0 }} />}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/*
              * 700 against 600, not 800 against 700.
              *
              * index.css loads Source Sans Pro at wght@400;600;700, so the old pair rendered
              * identically — a requested 800 falls back to the loaded 700 face — and the selected
              * node had no weight emphasis at all despite the code saying it did. 600 is loaded,
              * so the difference is now one the browser can actually draw.
              */}
            <Typography
              noWrap
              variant="body2"
              sx={{
                color: node.isActive ? cmmsColors.text : cmmsColors.mutedText,
                fontSize: 14,
                fontWeight: isSelected ? 700 : 600,
              }}
            >
              {node.name}
            </Typography>
            {node.linkedMachineName &&
              <Typography variant="caption" noWrap sx={{ color: cmmsColors.mutedText, display: "block", mt: 0.35 }}>
                OEE: {node.linkedMachineName}
              </Typography>
            }
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", ml: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Chip label={getAssetNodeTypeLabel(node.nodeType, intl)} size="small" variant="outlined" sx={getTypeChipSx(cmmsColors)} />
            <Chip
              label={intl.formatMessage(node.isActive ? messages.statusActive : messages.statusInactive)}
              size="small"
              sx={getStatusChipSx(cmmsColors, node.isActive)}
            />
          </Stack>
        </Box>
      </ListItemButton>

      {hasChildren &&
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {children.map((child) => (
            <AssetHierarchyTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
              expandedIds={expandedIds}
              onToggleNode={onToggleNode}
              visibleIds={visibleIds}
            />
          ))}
        </Collapse>
      }
    </Box>
  );
}

/**
 * Controlled: which nodes are open lives with the caller. Held per node, it reset to the default
 * every time a node remounted — on each collapse (`unmountOnExit`) and each tab switch.
 *
 * `visibleIds` narrows the tree to a search's matches and their paths; `null` shows everything.
 */
export default function AssetHierarchyTree({ nodes, selectedNodeId, onSelectNode, expandedIds, onToggleNode, visibleIds = null }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  if (!nodes?.length) {
    return (
      <Typography sx={{ color: cmmsColors.mutedText, fontSize: 14, mt: 2 }} variant="body2">
        <FormattedMessage {...messages.noAssetNodes} />
      </Typography>
    );
  }

  return (
    <List disablePadding sx={{ mt: 0.5 }}>
      {keepVisible(nodes, visibleIds).map((node) => (
        <AssetHierarchyTreeNode
          key={node.id}
          node={node}
          level={0}
          selectedNodeId={selectedNodeId}
          onSelectNode={onSelectNode}
          expandedIds={expandedIds}
          onToggleNode={onToggleNode}
          visibleIds={visibleIds}
        />
      ))}
    </List>
  );
}
